/**
 * Localized content resolvers.
 *
 * English registries (projects.ts, research.ts, …) are the canonical source and
 * are never modified for i18n. Russian is supplied as partial overlays in
 * src/content/ru/*, merged here field-by-field with an English fallback, so a
 * missing translation degrades to English rather than breaking the build.
 *
 * Slugs, ids, links between entities and the AI action protocol are
 * language-agnostic and always come from the English registries.
 */

import type { Lang } from '../i18n/config';
import { projects as projectsEn, type Project } from './projects.ts';
import { research as researchEn, type Research } from './research.ts';
import { knowledgeNodes as nodesEn } from './knowledge.ts';
import { fallbackAgents as agentsEn } from './agents.ts';
import { publications as publicationsEn, type Publication } from './publications.ts';
import { site as siteEn } from './site.ts';
import type { GraphNode, LabAgent } from '../lab/types';

import { projectsRu } from './ru/projects.ts';
import { researchRu } from './ru/research.ts';
import { nodesRu } from './ru/knowledge.ts';
import { agentsRu } from './ru/agents.ts';
import { publicationsRu } from './ru/publications.ts';
import { siteRu } from './ru/site.ts';
import { localizeAgentRoster } from '../lab/agent-localize.ts';

const pick = <T>(ru: T | undefined, en: T): T => (ru === undefined ? en : ru);

// ── Projects ──────────────────────────────────────────────────────────────

export type ProjectRu = Partial<
  Pick<Project, 'name' | 'tagline' | 'statusLabel' | 'domains' | 'summary' | 'body'>
> & { links?: { label: string; href: string }[] };

function localizeProject(p: Project, lang: Lang): Project {
  if (lang === 'en') return p;
  const r = projectsRu[p.slug];
  if (!r) return p;
  return {
    ...p,
    name: pick(r.name, p.name),
    tagline: pick(r.tagline, p.tagline),
    statusLabel: pick(r.statusLabel, p.statusLabel),
    domains: pick(r.domains, p.domains),
    summary: pick(r.summary, p.summary),
    body: pick(r.body, p.body),
    links: pick(r.links, p.links),
  };
}

export function getProjects(lang: Lang): Project[] {
  return projectsEn.map((p) => localizeProject(p, lang));
}
export function getProject(slug: string, lang: Lang): Project | undefined {
  const p = projectsEn.find((x) => x.slug === slug);
  return p ? localizeProject(p, lang) : undefined;
}

// ── Research ──────────────────────────────────────────────────────────────

export type ResearchRu = Partial<
  Pick<Research, 'title' | 'area' | 'hypothesis' | 'problem' | 'stateLabel' | 'methods' | 'findings'>
>;

function localizeResearch(r: Research, lang: Lang): Research {
  if (lang === 'en') return r;
  const t = researchRu[r.slug];
  if (!t) return r;
  return {
    ...r,
    title: pick(t.title, r.title),
    area: pick(t.area, r.area),
    hypothesis: pick(t.hypothesis, r.hypothesis),
    problem: pick(t.problem, r.problem),
    stateLabel: pick(t.stateLabel, r.stateLabel),
    methods: pick(t.methods, r.methods),
    findings: pick(t.findings, r.findings),
  };
}

export function getResearch(lang: Lang): Research[] {
  return researchEn.map((r) => localizeResearch(r, lang));
}
export function getResearchEntry(slug: string, lang: Lang): Research | undefined {
  const r = researchEn.find((x) => x.slug === slug);
  return r ? localizeResearch(r, lang) : undefined;
}

// ── Knowledge ─────────────────────────────────────────────────────────────

export type NodeRu = { label?: string };

export function getKnowledgeNodes(lang: Lang): GraphNode[] {
  if (lang === 'en') return nodesEn;
  return nodesEn.map((n) => ({ ...n, label: pick(nodesRu[n.id]?.label, n.label) }));
}
export function getNode(id: string, lang: Lang): GraphNode | undefined {
  const n = nodesEn.find((x) => x.id === id);
  if (!n) return undefined;
  return lang === 'en' ? n : { ...n, label: pick(nodesRu[n.id]?.label, n.label) };
}

// ── Agents ────────────────────────────────────────────────────────────────

export type AgentRu = Partial<Pick<LabAgent, 'role' | 'task'>>;

/** The static fallback registry, localized. Per-id overlay only — every id here is known ahead of time. */
export function getAgents(lang: Lang): LabAgent[] {
  if (lang === 'en') return agentsEn;
  return localizeAgentRoster(agentsEn, agentsRu, lang);
}

/**
 * Localize any agent roster, not just the static fallback registry — in
 * particular the Genesis-Live roster folded from the observer, whose ids
 * (`N0001…`) are not in the per-id overlay. Those rows are translated from
 * what the engine itself reported: the generation-stated role and the
 * row's structured task phrase (see `agent-localize.ts`), never from a
 * kind-derived job title the engine never claimed. Backward compatible with
 * `getAgents`: a static agent id still gets its full per-id role+task
 * overlay.
 */
export function localizeAgents(agents: LabAgent[], lang: Lang): LabAgent[] {
  if (lang === 'en') return agents;
  return localizeAgentRoster(agents, agentsRu, lang);
}

// ── Publications ──────────────────────────────────────────────────────────

export type PublicationRu = Partial<
  Pick<Publication, 'title' | 'venue' | 'statusLabel' | 'summary'>
>;

export function getPublications(lang: Lang): Publication[] {
  if (lang === 'en') return publicationsEn;
  return publicationsEn.map((p) => {
    const r = publicationsRu[p.slug];
    if (!r) return p;
    return {
      ...p,
      title: pick(r.title, p.title),
      venue: pick(r.venue, p.venue),
      statusLabel: pick(r.statusLabel, p.statusLabel),
      summary: pick(r.summary, p.summary),
    };
  });
}

// ── Site copy ─────────────────────────────────────────────────────────────

export type SiteCopy = {
  name: string;
  title: string;
  tagline: string;
  description: string;
};

export function getSiteCopy(lang: Lang): SiteCopy {
  const base: SiteCopy = {
    name: siteEn.name,
    title: siteEn.title,
    tagline: siteEn.tagline,
    description: siteEn.description,
  };
  if (lang === 'en') return base;
  return {
    name: pick(siteRu.name, base.name),
    title: pick(siteRu.title, base.title),
    tagline: pick(siteRu.tagline, base.tagline),
    description: pick(siteRu.description, base.description),
  };
}
